/**
 * PublicSettingsContext
 * ---------------------
 * Fetches `/api/settings/public` once on boot and applies the admin-
 * selected UI font to the document. Exposes the chosen key so any
 * component (e.g. the admin dropdown) can reflect the current value
 * and trigger a refresh after save.
 */
import * as React from "react";
import { api } from "../lib/api";
import { applyFont, DEFAULT_FONT_KEY } from "../lib/fonts";

interface PublicSettings {
  ui_font_family: string;
  marketplace_open: boolean;
  forum_open: boolean;
  allow_dealer_signup: boolean;
}

interface Ctx {
  settings: PublicSettings;
  refresh: () => Promise<void>;
}

const defaultSettings: PublicSettings = {
  ui_font_family: DEFAULT_FONT_KEY,
  marketplace_open: true,
  forum_open: true,
  allow_dealer_signup: true,
};

const PublicSettingsContext = React.createContext<Ctx>({
  settings: defaultSettings,
  refresh: async () => {},
});

export function PublicSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = React.useState<PublicSettings>(defaultSettings);

  const refresh = React.useCallback(async () => {
    try {
      const r = await api.get<PublicSettings>("/settings/public");
      setSettings(r.data);
      applyFont(r.data.ui_font_family);
    } catch {
      // Non-fatal: keep defaults and the default font.
      applyFont(DEFAULT_FONT_KEY);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <PublicSettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </PublicSettingsContext.Provider>
  );
}

export function usePublicSettings(): Ctx {
  return React.useContext(PublicSettingsContext);
}
